import {
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Button,
  ChipV2,
  DeleteConfirmationModal,
  Drawer,
  SearchInput,
} from "components";
import {
  CloseIcon,
  ExitDoorIcon,
  InfoIcon,
  LeftArrowIcon,
} from "components/icons";

import {
  useGetChatMembers,
  useUpdateChatMembers,
} from "controller/hooks/chat/useChatMembers";
import { useGlobalStore } from "controller/store/globalStore";
import { useAtomValue, useSetAtom } from "jotai";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { AddMemberItem, ChatMember, ScopeChangeItem } from "shared";
import * as yup from "yup";
import {
  addExternalEmail,
  availableCustomerMembersCount,
  externalEmails as externalEmailsAtom,
  filteredMembers,
  hasPendingChanges,
  isCustomerMembersSearching,
  memberSearchQuery,
  pendingMembersMap as pendingMembersMapAtom,
  removeExternalEmail,
  resetChatMembersDrawer,
  resetPendingChanges,
  scopeChanges,
  selectedMemberIds,
  setAllMembers,
  setInitialMemberIds,
  setMemberLevel,
  setMemberSearchQuery,
  setSelectedMemberIds,
  toggleMemberSelection,
} from "../../store/chatMembersStore";
import { AvailableMembersList } from "./AvailableMembersList";
import { MemberListItem } from "./MemberListItem";

const emailSchema = yup.string().email();
const EMPTY_FOOTER = <Box />;

// ─── Pure helpers for save logic ─────────────────────────────────────

function computeMemberDiffs(
  membersData: ChatMember[],
  selectedIds: Set<string>,
) {
  const initialIds = new Set(membersData.map((m) => m.user_id));
  const coreIds = new Set(
    membersData.filter((m) => m.member_source === "core").map((m) => m.user_id),
  );
  const removableIds = Array.from(initialIds)
    .filter((id) => !selectedIds.has(id))
    .filter((id) => !coreIds.has(id));
  const addedIds = Array.from(selectedIds).filter((id) => !initialIds.has(id));
  return { addedIds, removableIds };
}

function buildAddItems(
  addedIds: string[],
  pendingMap: Map<string, ChatMember>,
  extEmails: string[],
): AddMemberItem[] {
  return [
    ...addedIds.map((id) => ({
      user_id: id,
      member_type: pendingMap.get(id)?.member_type ?? ("customer" as const),
    })),
    ...extEmails.map((email) => ({
      user_id: email,
      member_type: "external" as const,
      email,
    })),
  ];
}

function buildScopeChanges(
  changes: Map<string, "ticket" | "thread">,
  threadId: string | undefined,
): ScopeChangeItem[] {
  return Array.from(changes.entries()).map(([userId, newLevel]) => ({
    user_id: userId,
    from_thread_id: newLevel === "ticket" ? (threadId ?? null) : null,
    to_thread_id: newLevel === "thread" ? (threadId ?? null) : null,
  }));
}

function buildSuccessMessage(
  result: { added_count: number; removed_count: number },
  scopeCount: number,
) {
  const parts: string[] = [];
  if (result.added_count > 0)
    parts.push(`${result.added_count} member(s) added`);
  if (result.removed_count > 0) parts.push(`${result.removed_count} removed`);
  if (scopeCount > 0) parts.push(`${scopeCount} scope updated`);
  return parts;
}

// ─── Search Header ───────────────────────────────────────────────────

interface SearchHeaderProps {
  isManageMode: boolean;
  membersData: ChatMember[] | undefined;
}

const SearchHeader = memo(
  ({ isManageMode, membersData }: SearchHeaderProps) => {
    const theme = useTheme();
    const { orange } = theme.palette.primitive.system;

    const searchQuery = useAtomValue(memberSearchQuery);
    const setSearchQuery = useSetAtom(setMemberSearchQuery);
    const extEmails = useAtomValue(externalEmailsAtom);
    const addExtEmail = useSetAtom(addExternalEmail);
    const removeExtEmail = useSetAtom(removeExternalEmail);
    const matchingMembers = useAtomValue(filteredMembers);
    const availableMembersCount = useAtomValue(availableCustomerMembersCount);
    const isSearchingCustomerMembers = useAtomValue(isCustomerMembersSearching);

    const trimmedQuery = searchQuery.trim();

    const isValidEmail = useMemo(() => {
      if (!trimmedQuery) return false;
      return emailSchema.isValidSync(trimmedQuery);
    }, [trimmedQuery]);

    const isEmailAlreadyAdded = useMemo(() => {
      if (!isValidEmail) return false;
      const lower = trimmedQuery?.toLowerCase();
      if (extEmails.includes(lower)) return true;
      return (membersData ?? []).some((m) => m.email?.toLowerCase() === lower);
    }, [isValidEmail, trimmedQuery, extEmails, membersData]);

    const hasMatchingMembers =
      matchingMembers.length > 0 || availableMembersCount > 0;

    const showEmailDropdown =
      isManageMode &&
      isValidEmail &&
      !isEmailAlreadyAdded &&
      !hasMatchingMembers &&
      !isSearchingCustomerMembers;

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [setSearchQuery],
    );

    const handleAddExternalEmail = useCallback(() => {
      if (!showEmailDropdown) return;
      addExtEmail(trimmedQuery);
      setSearchQuery("");
    }, [showEmailDropdown, trimmedQuery, addExtEmail, setSearchQuery]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && showEmailDropdown) {
          e.preventDefault();
          handleAddExternalEmail();
        }
      },
      [showEmailDropdown, handleAddExternalEmail],
    );

    return (
      <Box
        sx={{
          padding: "12px 16px",
          borderTop: `1px solid ${theme.palette.neutral["015"]}`,
        }}
      >
        <SearchInput
          fullWidth
          name="member-search"
          placeholder={
            isManageMode
              ? "Search participants or add an external mail ID"
              : "Search participants"
          }
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          hideHelperBox
          inputProps={{ "aria-label": "Search participants" }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.neutral["005"],
              "& fieldset": {
                borderColor: theme.palette.neutral["010"],
              },
            },
          }}
        />

        {showEmailDropdown && (
          <Paper
            elevation={2}
            sx={{ mt: "4px", borderRadius: "8px", overflow: "hidden" }}
          >
            <Box
              onClick={handleAddExternalEmail}
              sx={{
                padding: "10px 16px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.neutral["005"],
                },
              }}
            >
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.primary.main }}
              >
                Use this address : @{searchQuery}
              </Typography>
            </Box>
            <Box sx={{ padding: "10px 16px" }}>
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.lightBg.low }}
              >
                We couldn&apos;t find the person you were looking for.
              </Typography>
            </Box>
          </Paper>
        )}

        {isManageMode && extEmails.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              mt: "8px",
            }}
          >
            {extEmails.map((email) => (
              <ChipV2
                key={email}
                variant="info"
                clearable
                onClear={() => removeExtEmail(email)}
              >
                {email}
              </ChipV2>
            ))}
          </Box>
        )}

        {isManageMode && extEmails.length > 0 && (
          <Stack
            direction="row"
            sx={{
              mt: "8px",
              backgroundColor: orange[50],
              border: `1px solid ${orange[800]}`,
              borderRadius: "4px",
              px: "16px",
              py: "8px",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <InfoIcon sx={{ fontSize: "20px" }} htmlColor={orange[800]} />
            <Typography variant="body-small" sx={{ color: orange[800] }}>
              This is an external mail ID
            </Typography>
          </Stack>
        )}
      </Box>
    );
  },
);

SearchHeader.displayName = "SearchHeader";

// ─── View Mode Body ──────────────────────────────────────────────────

interface ViewModeBodyProps {
  totalCount: number;
  onManage: () => void;
  onRemove: (userId: string) => void;
  searchQuery: string;
  isLoading: boolean;
  isDisabled?: boolean;
}

const ViewModeBody = memo(
  ({
    totalCount,
    onManage,
    onRemove,
    searchQuery,
    isLoading,
    isDisabled,
  }: ViewModeBodyProps) => {
    const theme = useTheme();
    const members = useAtomValue(filteredMembers);

    return (
      <>
        {!isDisabled && (
          <Box
            sx={{
              padding: "8px 24px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: theme.palette.primitive.brand.purple[50],
              borderBottom: `1px solid ${theme.palette.primitive.brand.purple[100]}`,
            }}
          >
            <Button variant="ghost" isLink fullWidth onClick={onManage}>
              Add participants
            </Button>
          </Box>
        )}

        {members.map((member) => {
          const level =
            member.member_source === "cc_thread" ? "thread" : "ticket";
          return (
            <MemberListItem
              key={member.user_id}
              member={member}
              isSelected={false}
              onToggle={onManage}
              level={level}
              selectable={false}
              onRemove={onRemove}
            />
          );
        })}

        {searchQuery.length > 0 && !isLoading && members.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: "24px",
            }}
          >
            <Typography
              variant="body-medium"
              sx={{ color: theme.palette.lightBg.low }}
            >
              No results found
            </Typography>
          </Box>
        )}
      </>
    );
  },
);

ViewModeBody.displayName = "ViewModeBody";

// ─── Manage Mode Body ────────────────────────────────────────────────

interface ManageModeBodyProps {
  totalCount: number;
  open: boolean;
  membersData: ChatMember[] | undefined;
  threadId?: string;
  isLoading: boolean;
  handleLevelChange: (userId: string, level: "ticket" | "thread") => void;
}

const ManageModeBody = memo(
  ({
    totalCount,
    open,
    membersData,
    threadId,
    isLoading,
    handleLevelChange,
  }: ManageModeBodyProps) => {
    const members = useAtomValue(filteredMembers);
    const selectedIds = useAtomValue(selectedMemberIds);
    const toggleSelection = useSetAtom(toggleMemberSelection);
    const memberScopeChanges = useAtomValue(scopeChanges);

    return (
      <>
        {members.map((member) => {
          const originalLevel =
            member.member_source === "cc_thread" ? "thread" : "ticket";
          const effectiveLevel =
            memberScopeChanges.get(member.user_id) ?? originalLevel;

          return (
            <MemberListItem
              key={member.user_id}
              member={member}
              isSelected={selectedIds.has(member.user_id)}
              onToggle={toggleSelection}
              level={effectiveLevel}
              onLevelChange={
                threadId && member.member_source === "cc_thread"
                  ? handleLevelChange
                  : undefined
              }
            />
          );
        })}

        <AvailableMembersList
          open={open}
          membersData={membersData}
          threadId={threadId}
          filteredMembersCount={members.length}
          isMembersLoading={isLoading}
        />
      </>
    );
  },
);

ManageModeBody.displayName = "ManageModeBody";

// ─── Main Drawer ─────────────────────────────────────────────────────

interface ChatMembersDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  serviceId: string;
  threadId?: string;
  isDisabled?: boolean;
}

/**
 * ChatMembersDrawer - Two-screen participant management
 *
 * View mode (default): Read-only list of current participants with search.
 * Manage mode: Full add/remove/scope-change capabilities with save/cancel.
 */
export const ChatMembersDrawer = memo(
  ({
    open,
    onClose,
    onOpen,
    serviceId,
    threadId,
    isDisabled,
  }: ChatMembersDrawerProps) => {
    const theme = useTheme();

    // ─── Mode state ──────────────────────────────────────────────────
    const [mode, setMode] = useState<"view" | "manage">("view");
    const isViewMode = mode === "view";

    useEffect(() => {
      if (!open) setMode("view");
    }, [open]);

    // ─── Atoms ───────────────────────────────────────────────────────
    const searchQuery = useAtomValue(memberSearchQuery);
    const setSearchQuery = useSetAtom(setMemberSearchQuery);
    const selectedIds = useAtomValue(selectedMemberIds);
    const setSelectedIds = useSetAtom(setSelectedMemberIds);
    const hasChanges = useAtomValue(hasPendingChanges);
    const extEmails = useAtomValue(externalEmailsAtom);
    const memberScopeChanges = useAtomValue(scopeChanges);
    const pendingMembers = useAtomValue(pendingMembersMapAtom);
    const setMembers = useSetAtom(setAllMembers);
    const setInitialIds = useSetAtom(setInitialMemberIds);
    const resetDrawer = useSetAtom(resetChatMembersDrawer);
    const resetPending = useSetAtom(resetPendingChanges);
    const changeMemberLevel = useSetAtom(setMemberLevel);

    // ─── Query hooks ─────────────────────────────────────────────────
    const { data: membersData, isLoading } = useGetChatMembers(
      serviceId,
      threadId,
    );
    const { mutateAsync: updateMembers } = useUpdateChatMembers();

    const [isSaving, setIsSaving] = useState(false);

    // ─── Remove member state ──────────────────────────────────────────
    const [memberToRemove, setMemberToRemove] = useState<ChatMember | null>(
      null,
    );
    const [isRemoving, setIsRemoving] = useState(false);

    const handleRemoveClick = useCallback(
      (userId: string) => {
        const member = membersData?.find((m) => m.user_id === userId);
        if (member) setMemberToRemove(member);
      },
      [membersData],
    );

    const handleConfirmRemove = useCallback(async () => {
      if (!memberToRemove) return;
      setIsRemoving(true);
      try {
        const result = await updateMembers({
          service_id: serviceId,
          thread_id: threadId,
          remove: [memberToRemove.user_id],
        });
        if (result.removed_count > 0) {
          toast.success("Participant removed");
        }
        setMemberToRemove(null);
      } catch {
        toast.error("Failed to remove participant");
      } finally {
        setIsRemoving(false);
      }
    }, [memberToRemove, updateMembers, serviceId, threadId]);

    const handleCancelRemove = useCallback(() => {
      setMemberToRemove(null);
    }, []);

    // ─── Leave conversation state ─────────────────────────────────────
    const currentUserId = useGlobalStore((state) => state.resourceId);

    const canLeaveConversation = useMemo(() => {
      if (!currentUserId || !membersData) return false;
      const currentMember = membersData.find(
        (m) => m.user_id === currentUserId,
      );
      return !!currentMember && currentMember.member_source !== "core";
    }, [currentUserId, membersData]);

    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    const handleLeaveClick = useCallback(() => {
      setShowLeaveConfirm(true);
    }, []);

    const handleCancelLeave = useCallback(() => {
      setShowLeaveConfirm(false);
    }, []);

    const handleConfirmLeave = useCallback(async () => {
      if (!currentUserId) return;
      setIsLeaving(true);
      try {
        const result = await updateMembers({
          service_id: serviceId,
          thread_id: threadId,
          remove: [currentUserId],
        });
        if (result.removed_count > 0) {
          toast.success("You have left the conversation");
        }
        setShowLeaveConfirm(false);
        onClose();
      } catch {
        toast.error("Failed to leave conversation");
      } finally {
        setIsLeaving(false);
      }
    }, [currentUserId, updateMembers, serviceId, threadId, onClose]);

    // ─── Initialize members data ─────────────────────────────────────
    useEffect(() => {
      if (membersData) {
        setMembers(membersData);
        const currentIds = new Set(membersData.map((m) => m.user_id));
        setSelectedIds(currentIds);
        setInitialIds(currentIds);
      }
    }, [membersData, setMembers, setSelectedIds, setInitialIds]);

    const totalCount = membersData?.length ?? 0;

    // ─── Handlers ────────────────────────────────────────────────────
    const handleLevelChange = useCallback(
      (userId: string, level: "ticket" | "thread") => {
        changeMemberLevel({ userId, level });
      },
      [changeMemberLevel],
    );

    const handleCancel = useCallback(() => {
      if (membersData) {
        const currentIds = new Set(membersData.map((m) => m.user_id));
        setSelectedIds(currentIds);
      }
      setSearchQuery("");
      onClose();
    }, [membersData, setSelectedIds, setSearchQuery, onClose]);

    const handleBackToView = useCallback(() => {
      resetPending();
      setMode("view");
    }, [resetPending]);

    // ─── Save ────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
      if (!hasChanges || !membersData) return;

      setIsSaving(true);
      const { addedIds, removableIds } = computeMemberDiffs(
        membersData,
        selectedIds,
      );
      const addItems = buildAddItems(addedIds, pendingMembers, extEmails);
      const scopeChangeItems = buildScopeChanges(memberScopeChanges, threadId);
      const hasUpdates =
        addItems.length > 0 ||
        removableIds.length > 0 ||
        scopeChangeItems.length > 0;

      try {
        if (hasUpdates) {
          const result = await updateMembers({
            service_id: serviceId,
            thread_id: threadId,
            add: addItems.length > 0 ? addItems : undefined,
            remove: removableIds.length > 0 ? removableIds : undefined,
            scope_changes:
              scopeChangeItems.length > 0 ? scopeChangeItems : undefined,
          });
          const parts = buildSuccessMessage(result, scopeChangeItems.length);
          if (parts.length > 0) toast.success(parts.join(", "));
        }

        resetPending();
        onClose();
      } catch {
        toast.error("Failed to save member changes");
      } finally {
        setIsSaving(false);
      }
    }, [
      hasChanges,
      membersData,
      pendingMembers,
      selectedIds,
      extEmails,
      memberScopeChanges,
      updateMembers,
      serviceId,
      threadId,
      resetPending,
      onClose,
    ]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        resetDrawer();
      };
    }, [resetDrawer]);

    // ─── View mode custom header (title + badge + search) ────────────
    const viewHeader = (
      <>
        <Box
          sx={{
            px: "24px",
            py: "12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}
          >
            Participants
            <ChipV2
              variant="neutral"
              containerSx={{
                borderRadius: "100px",
                px: "12px",
                py: "2px",
                height: "16px",
              }}
            >
              {totalCount}
            </ChipV2>
          </Typography>
          {canLeaveConversation && (
            <IconButton
              onClick={handleLeaveClick}
              aria-label="Leave conversation"
              sx={{
                "&:hover": {
                  borderRadius: "100px",
                },
              }}
            >
              <ExitDoorIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            aria-label="Close drawer"
            sx={{
              "&:hover": {
                backgroundColor: theme.palette.error.main,
                transition: "all 0.5s",
                borderRadius: "100px",
                color: theme.palette.neutral.main,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <SearchHeader isManageMode={false} membersData={membersData} />
      </>
    );

    // ─── Manage mode custom header (includes search) ────────────────
    const manageHeader = (
      <>
        <Box
          sx={{
            pr: "24px",
            pl: "12px",
            py: "12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <IconButton
            onClick={handleBackToView}
            aria-label="Back to participants"
            size="small"
          >
            <LeftArrowIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flex: 1 }}>
            Add participants
          </Typography>
          <IconButton
            onClick={handleCancel}
            aria-label="Close drawer"
            sx={{
              "&:hover": {
                backgroundColor: theme.palette.error.main,
                transition: "all 0.5s",
                borderRadius: "100px",
                color: theme.palette.neutral.main,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <SearchHeader isManageMode membersData={membersData} />
      </>
    );

    const drawerProps = useMemo(() => {
      if (isViewMode) {
        return {
          title: undefined,
          header: viewHeader,
          footer: EMPTY_FOOTER,
          onClose,
          primaryButtonText: undefined,
          onPrimaryButtonClick: undefined,
          isPrimaryButtonDisabled: undefined,
          isLoading: false,
          secondaryButtonText: undefined,
          onSecondaryButtonClick: undefined,
          isSecondaryButtonDisabled: undefined,
        };
      }
      return {
        title: undefined,
        header: manageHeader,
        footer: undefined,
        onClose: handleCancel,
        primaryButtonText: "Save changes" as const,
        onPrimaryButtonClick: handleSave,
        isPrimaryButtonDisabled: !hasChanges || isSaving,
        isLoading: isSaving,
        secondaryButtonText: "Cancel" as const,
        onSecondaryButtonClick: handleCancel,
        isSecondaryButtonDisabled: isSaving,
      };
    }, [
      isViewMode,
      viewHeader,
      onClose,
      manageHeader,
      handleCancel,
      handleSave,
      hasChanges,
      isSaving,
    ]);

    return (
      <>
        <Drawer
          anchor="right"
          open={open}
          onClose={drawerProps.onClose}
          onOpen={onOpen}
          title={drawerProps.title}
          header={drawerProps.header}
          drawerStyle={{ width: "404px" }}
          footer={drawerProps.footer}
          primaryButtonText={drawerProps.primaryButtonText}
          onPrimaryButtonClick={drawerProps.onPrimaryButtonClick}
          isPrimaryButtonDisabled={drawerProps.isPrimaryButtonDisabled}
          isLoading={drawerProps.isLoading}
          secondaryButtonText={drawerProps.secondaryButtonText}
          onSecondaryButtonClick={drawerProps.onSecondaryButtonClick}
          isSecondaryButtonDisabled={drawerProps.isSecondaryButtonDisabled}
          headerStyle={{ borderBottom: "none" }}
          bodySx={{ overflow: "auto" }}
        >
          {isViewMode ? (
            <ViewModeBody
              totalCount={totalCount}
              onManage={() => setMode("manage")}
              onRemove={handleRemoveClick}
              searchQuery={searchQuery}
              isLoading={isLoading}
              isDisabled={isDisabled}
            />
          ) : (
            <ManageModeBody
              totalCount={totalCount}
              open={open}
              membersData={membersData}
              threadId={threadId}
              isLoading={isLoading}
              handleLevelChange={handleLevelChange}
            />
          )}
        </Drawer>

        <DeleteConfirmationModal
          open={!!memberToRemove}
          onClose={handleCancelRemove}
          title="Remove participant"
          tagline={`Remove ${memberToRemove?.display_name || memberToRemove?.email || "this participant"} from this conversation?`}
          primaryButtonText="Remove"
          secondaryButtonText="Cancel"
          type="error"
          isLoading={isRemoving}
          onPrimaryButtonClick={handleConfirmRemove}
          onSecondaryButtonClick={handleCancelRemove}
        />

        <DeleteConfirmationModal
          open={showLeaveConfirm}
          onClose={handleCancelLeave}
          title="Leave conversation"
          tagline="Are you sure you want to leave this conversation? You will no longer receive messages."
          primaryButtonText="Leave"
          secondaryButtonText="Cancel"
          type="error"
          isLoading={isLeaving}
          onPrimaryButtonClick={handleConfirmLeave}
          onSecondaryButtonClick={handleCancelLeave}
        />
      </>
    );
  },
);

ChatMembersDrawer.displayName = "ChatMembersDrawer";
